<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCartStore } from '../stores/cart';
import { CommerceLineItem } from '../types/commerce';

interface Variant {
    id: number;
    title: string;
    price: string;
    available: boolean;
}

const props = defineProps<{
    variants: [Variant, ...Variant[]];
}>();

const { t } = useI18n({ useScope: 'global' });
const cart = useCartStore();
const isAdding = ref(false);

const selectedVariantId = ref<number>(props.variants[0].id);
const selectedVariant = computed<Variant | undefined>(
    () => props.variants.find((v) => v.id === selectedVariantId.value),
);

const lineItem = computed<CommerceLineItem | null>(() =>
    cart.cart?.lineItems.find((item) => item.purchasableId === selectedVariantId.value) ?? null,
);

async function addToCart(): Promise<void> {
    if (!selectedVariant.value?.available) return;
    isAdding.value = true;
    try {
        await cart.addToCart(selectedVariantId.value, 1);
    } finally {
        isAdding.value = false;
    }
}

const selectId = `variant-select-${props.variants[0]?.id}`;
</script>

<template>
    <form v-if="variants" class="product-purchase" @submit.prevent="addToCart()">
        <template v-if="variants.length > 1">
            <div v-if="variants.length > 0" class="product-purchase__variant-select">
                <label :for="selectId" class="visually-hidden">
                    {{ t('addToCart.selectLabel') }}
                </label>
                <select
                    :id="selectId"
                    v-model="selectedVariantId"
                    class="form-select form-select-sm"
                >
                    <option
                        v-for="variant in variants"
                        :key="variant.id"
                        :value="variant.id"
                    >{{ variant.title }}</option>
                </select>
            </div>
        </template>

        <p v-if="selectedVariant" class="product-purchase__price">
            {{ selectedVariant.price }}
        </p>

        <button
            type="submit"
            class="btn btn-primary add-to-cart-button"
            :disabled="isAdding || !selectedVariant?.available"
            :class="{ 'add-to-cart-button--out-of-stock': !selectedVariant?.available }"
            :aria-busy="isAdding"
            aria-live="polite"
            aria-atomic="true"
        >
            <span v-if="isAdding" class="add-to-cart-button__spinner" aria-hidden="true" />
            <span v-else-if="!selectedVariant?.available">{{ t('addToCart.outOfStock') }}</span>
            <span v-else-if="lineItem">{{ t('addToCart.inCart', { qty: lineItem.qty }) }}</span>
            <span v-else>{{ t('addToCart.default') }}</span>
        </button>
    </form>
</template>
